import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../../contexts/AuthContext';
import Layout from '../Layout/Layout';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js';
import { useNavigate, Link } from 'react-router-dom';
import { subscriptionService } from '../../services/subscriptionService';
import DashboardLayout from '../Layout/DashboardLayout';
import { Card } from '../common/UIComponents';
import CostByTagChart from './CostByTagChart';
import UpgradeBar from '../common/UpgradeBar';
import CalendarPage from '../../pages/CalendarPage';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

const DashboardContainer = styled.div`
  padding: 24px;
  background: var(--background);
  color: var(--text-primary);
`;

const PageTitle = styled.h1`
  font-size: 32px;
  margin-bottom: 24px;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
`;

const StatsCard = styled(Card)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px var(--card-shadow);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatTitle = styled.h3`
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const StatIcon = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: var(--primary-light);
  color: var(--primary);
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
`;

const StatChange = styled.div`
  font-size: 14px;
  color: ${props => props.increase ? 'var(--success)' : 'var(--error)'};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UpgradeLink = styled.button`
  color: var(--text-primary);
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
`;

const ChartCard = styled(Card)`
  padding: 24px;
  height: 400px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 24px;
  color: var(--text-primary);
`;

const DueNextSection = styled.div`
  margin-top: 24px;
`;

const DueNextCard = styled.div`
  background: var(--surface);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 4px var(--card-shadow);
`;

const DueNextHeader = styled.h2`
  font-size: 18px;
  margin-bottom: 24px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  text-align: center;
  gap: 16px;
`;

const NewSubscriptionButton = styled.button`
  background: var(--surface);
  color: var(--text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 4px var(--card-shadow);

  &:hover {
    background: var(--hover);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;
  gap: 8px;
`;

const PerPageSelect = styled.select`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-primary);
`;

const RecentActivity = styled(Card)`
  padding: 24px;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: var(--surface-hover);
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const ActivityMeta = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ActivityAmount = styled.div`
  font-weight: 600;
  color: ${props => props.type === 'credit' ? 'var(--success)' : 'var(--error)'};
`;

const Container = styled.div`
  padding: 24px;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Section = styled.div`
  background: var(--surface);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 32px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: var(--gradient-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  margin-bottom: 16px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

const EmptyText = styled.p`
  color: var(--text-secondary);
  font-size: 18px;
  line-height: 1.6;
  max-width: 400px;
  margin-bottom: 24px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Label = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
`;

const SubscriptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const SubscriptionCard = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CycleTag = styled.span`
  background: var(--primary-light);
  color: var(--primary);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  width: fit-content;
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
`;

const DetailValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 14px;
`;

const AddSubscriptionCard = styled(SubscriptionCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  cursor: pointer;
  background: var(--surface-hover);
  border: 2px dashed var(--border);

  &:hover {
    border-color: var(--primary);
    background: var(--surface);
  }
`;

const AddIcon = styled.div`
  font-size: 32px;
  margin-bottom: 16px;
  color: var(--primary);
`;

const AddText = styled.div`
  color: var(--text-primary);
  font-weight: 500;
`;

// Add these styled components for the table
const TableContainer = styled.div`
  background: var(--surface);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border);
  margin-top: 16px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text-secondary);
  font-weight: 500;
  background: var(--surface);
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  padding: 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text-primary);
  align-items: center;

  &:hover {
    background: var(--hover);
  }
`;

const Button = styled.button`
  background: var(--surface);
  color: var(--text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 4px var(--card-shadow);

  &:hover {
    background: var(--hover);
  }
`;

const CalendarButton = styled.button`
  background: var(--surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover);
    transform: translateY(-1px);
  }
`;

// Update the DateCell styled component
const DateCell = styled.div`
  color: var(--error);
  font-weight: 500;
`;

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [activeSubscriptions, setActiveSubscriptions] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [groupedSubscriptions, setGroupedSubscriptions] = useState({
    weekly: [],
    monthly: [],
    yearly: []
  });
  const [upcomingPayments, setUpcomingPayments] = useState('0.00');
  const [lineChartData, setLineChartData] = useState({
    labels: [],
    datasets: []
  });
  const [barChartData, setBarChartData] = useState({
    labels: [],
    datasets: []
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarSubscriptions, setCalendarSubscriptions] = useState([]);

  const getNextPaymentDate = (subscription) => {
    const startDate = new Date(subscription.start_date);
    const today = new Date();
    
    switch (subscription.cycle.toLowerCase()) {
      case 'weekly':
        // Calculate days since start
        const daysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
        const weeksElapsed = Math.floor(daysSinceStart / 7);
        // Get next payment date
        const nextWeeklyDate = new Date(startDate);
        nextWeeklyDate.setDate(startDate.getDate() + ((weeksElapsed + 1) * 7));
        return nextWeeklyDate;
        
      case 'monthly':
        const nextMonthlyDate = new Date(startDate);
        // Find next monthly payment date
        while (nextMonthlyDate <= today) {
          nextMonthlyDate.setMonth(nextMonthlyDate.getMonth() + 1);
        }
        return nextMonthlyDate;
        
      case 'yearly':
        const nextYearlyDate = new Date(startDate);
        // Find next yearly payment date
        while (nextYearlyDate <= today) {
          nextYearlyDate.setFullYear(nextYearlyDate.getFullYear() + 1);
        }
        return nextYearlyDate;
        
      default:
        return startDate;
    }
  };

  // Update the upcoming payments calculation
  const calculateUpcomingPayments = (subscriptions) => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    return subscriptions.reduce((total, sub) => {
      const nextPayment = getNextPaymentDate(sub);
      
      // Check if payment is due in the next 7 days
      if (nextPayment >= today && nextPayment <= nextWeek) {
        // For weekly subscriptions, add one payment
        if (sub.cycle.toLowerCase() === 'weekly') {
          return total + parseFloat(sub.price);
        }
        
        // For monthly subscriptions, add one payment if due
        if (sub.cycle.toLowerCase() === 'monthly') {
          return total + parseFloat(sub.price);
        }
        
        // For yearly subscriptions, add one payment if due
        if (sub.cycle.toLowerCase() === 'yearly') {
          return total + parseFloat(sub.price);
        }
      }
      return total;
    }, 0);
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        
        const allSubscriptions = await subscriptionService.getAllSubscriptions(user.id);
        
        // Format subscriptions for calendar
        const formattedSubs = allSubscriptions.map(sub => ({
          ...sub,
          next_payment: getNextPaymentDate(sub).toISOString(),
          tags: Array.isArray(sub.tags) ? sub.tags : 
                typeof sub.tags === 'string' ? sub.tags.split(',').map(tag => tag.trim()) : 
                []
        }));

        setCalendarSubscriptions(formattedSubs);

        // Calculate basic stats
        const totalCount = allSubscriptions.length;
        const activeCount = allSubscriptions.filter(sub => sub.status === 'active').length;
        
        // Get today and next week's date for comparison
        const today = new Date();
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);

        // Group subscriptions that are due in next 7 days
        const grouped = allSubscriptions.reduce((acc, sub) => {
          const nextPaymentDate = getNextPaymentDate(sub);
          
          // Only include if next payment is within the next 7 days
          if (nextPaymentDate >= today && nextPaymentDate <= nextWeek) {
            const cycle = sub.cycle.toLowerCase();
            if (['weekly', 'monthly', 'yearly'].includes(cycle)) {
              acc[cycle] = [...(acc[cycle] || []), {
                ...sub,
                start_date: new Date(sub.start_date).toLocaleDateString(),
                next_payment: nextPaymentDate.toLocaleDateString()
              }];
            }
          }
          return acc;
        }, { weekly: [], monthly: [], yearly: [] });

        setGroupedSubscriptions(grouped);

        // Update upcoming payments
        const upcomingTotal = calculateUpcomingPayments(allSubscriptions);
        setUpcomingPayments(upcomingTotal.toFixed(2));

        // Calculate costs per tag
        const costsByTag = allSubscriptions.reduce((acc, sub) => {
          if (sub.tags) {
            sub.tags.forEach(tag => {
              acc[tag] = (acc[tag] || 0) + parseFloat(sub.price);
            });
          }
          return acc;
        }, {});

        // Calculate next months costs
        const nextMonthsCosts = Array.from({ length: 12 }, (_, i) => {
          const month = new Date();
          month.setMonth(month.getMonth() + i);
          
          return allSubscriptions.reduce((total, sub) => {
            const nextPayment = getNextPaymentDate(sub);
            if (nextPayment.getMonth() === month.getMonth() && 
                nextPayment.getFullYear() === month.getFullYear()) {
              return total + parseFloat(sub.price);
            }
            return total;
          }, 0);
        });

        // Update state with calculated stats
        setSubscriptionCount(totalCount);
        setActiveSubscriptions(activeCount);
        
        // Update chart data
        setLineChartData({
          labels: Array.from({ length: 12 }, (_, i) => {
            const month = new Date();
            month.setMonth(month.getMonth() + i);
            return month.toLocaleString('default', { month: 'short' });
          }),
          datasets: [{
            label: 'Monthly Costs',
            data: nextMonthsCosts,
            borderColor: '#4F46E5', // Bright indigo
            backgroundColor: 'rgba(79, 70, 229, 0.1)', // Light indigo with transparency
            fill: true,
            tension: 0.4,
            pointRadius: 6,
            pointBackgroundColor: '#4F46E5',
            pointBorderColor: '#fff',
            pointHoverRadius: 8,
            pointHoverBackgroundColor: '#4F46E5',
            pointHoverBorderColor: '#fff',
            borderWidth: 3
          }]
        });

        setBarChartData({
          labels: Object.keys(costsByTag),
          datasets: [{
            label: 'Cost per Tag',
            data: Object.values(costsByTag),
            backgroundColor: [
              '#4F46E5', // Indigo
              '#06B6D4', // Cyan
              '#10B981', // Emerald
              '#F59E0B', // Amber
              '#EF4444', // Red
              '#8B5CF6', // Purple
              '#EC4899', // Pink
              '#14B8A6', // Teal
              '#F97316', // Orange
              '#6366F1'  // Blue
            ],
            borderColor: '#ffffff',
            borderWidth: 2,
            borderRadius: 8,
            hoverOffset: 4
          }]
        });

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [user]);

  // Calculate totals for stats
  const totalSubscriptions = subscriptions.length;
  const monthlyTotal = subscriptions
    .filter(sub => sub.cycle === 'Monthly')
    .reduce((sum, sub) => sum + sub.price, 0);
  const yearlyTotal = monthlyTotal * 12;

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: 'var(--text-primary)',
          font: {
            size: 12,
            weight: '500'
          },
          padding: 10,
          callback: (value) => `$${value}`
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'var(--text-primary)',
          font: {
            size: 12,
            weight: '500'
          },
          padding: 10
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          color: 'var(--text-primary)',
          font: {
            size: 12,
            weight: '500'
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
          weight: '600'
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => `$${context.parsed.y.toFixed(2)}`
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        },
        ticks: {
          color: 'var(--text-primary)',
          font: {
            size: 12,
            weight: '500'
          },
          padding: 10,
          callback: (value) => `$${value}`
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'var(--text-primary)',
          font: {
            size: 12,
            weight: '500'
          },
          padding: 10
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
          weight: '600'
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => `$${context.parsed.y.toFixed(2)}`
        }
      }
    }
  };

  const handleNewSubscription = () => {
    navigate('/subscriptions/create');
  };

  return (
    <Layout>
      <DashboardContainer>
        <UpgradeBar />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <PageTitle>Dashboard</PageTitle>
        </div>
        
        

        <DashboardGrid>
          <StatsCard>
            <StatHeader>
              <StatTitle>Total Subscriptions</StatTitle>
              <StatIcon>📊</StatIcon>
            </StatHeader>
            <StatValue>{subscriptionCount}</StatValue>
            <StatChange increase={activeSubscriptions > 0}>
              {activeSubscriptions} active subscriptions
            </StatChange>
          </StatsCard>

          <StatsCard>
            <StatHeader>
              <StatTitle>Active Subscriptions</StatTitle>
              <StatIcon>✨</StatIcon>
            </StatHeader>
            <StatValue>{activeSubscriptions}</StatValue>
            <StatChange increase={activeSubscriptions > subscriptionCount - activeSubscriptions}>
              {((activeSubscriptions / subscriptionCount) * 100).toFixed(1)}% of total
            </StatChange>
          </StatsCard>

          <StatsCard>
            <StatHeader>
              <StatTitle>Upcoming Payments</StatTitle>
              <StatIcon>📅</StatIcon>
            </StatHeader>
            <StatValue>${upcomingPayments}</StatValue>
            <StatChange>
              Next 7 days
            </StatChange>
          </StatsCard>
        </DashboardGrid>

        <ChartsGrid>
          <ChartCard>
            <SectionTitle>Next Months Costs</SectionTitle>
            <div style={{ height: '300px' }}>
              <Line data={lineChartData} options={lineChartOptions} />
            </div>
          </ChartCard>

          <ChartCard>
            <SectionTitle>Subscriptions Costs per Tag</SectionTitle>
            <div style={{ height: '300px' }}>
              <Bar data={barChartData} options={barChartOptions} />
            </div>
          </ChartCard>
        </ChartsGrid>

        <Container>
          <Section>
            <SectionTitle>Subscriptions Due Next Week</SectionTitle>
            {Object.values(groupedSubscriptions).some(subs => subs.length > 0) ? (
              <TableContainer>
                <TableHeader>
                  <div>Name</div>
                  <div>Provider</div>
                  <div>Cycle</div>
                  <div>Start Date</div>
                  <div>Due Date</div>
                  <div>Amount</div>
                </TableHeader>
                {Object.values(groupedSubscriptions)
                  .flat()
                  .sort((a, b) => new Date(a.next_payment) - new Date(b.next_payment))
                  .map(subscription => (
                    <TableRow key={subscription.id}>
                      <div>{subscription.name}</div>
                      <div>{subscription.provider}</div>
                      <div>
                        <CycleTag>{subscription.cycle}</CycleTag>
                      </div>
                      <div>{subscription.start_date}</div>
                      <DateCell>
                        {subscription.next_payment}
                      </DateCell>
                      <div>${subscription.price}</div>
                    </TableRow>
                  ))}
              </TableContainer>
            ) : (
              <EmptyState>
                <EmptyIcon>💰</EmptyIcon>
                <EmptyText>No subscriptions due in the next 7 days</EmptyText>
              </EmptyState>
            )}
            
            {/* Add New Subscription button at the end */}
            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <Button onClick={handleNewSubscription}>
                <AddIcon style={{ marginRight: '8px' }}>➕</AddIcon>
                New Subscription
              </Button>
            </div>
          </Section>
        </Container>

       
      </DashboardContainer>
    </Layout>
  );
};

export default Dashboard; 